import { Zap } from "lucide-react";
import { Card } from "../ui/Card";
import { cn } from "../../lib/cn";
import { formatNumber } from "../../lib/format";
import { confidenceSegments, energyBudgetScale } from "../../screens/plan-utils";
import { ConfidenceChip } from "../progress/ConfidenceChip";
import { expenditureBadge, type EngineSnapshot } from "../progress/utils";

export interface EnergyBudgetCardProps {
  readonly snapshot: EngineSnapshot;
}

export function EnergyBudgetCard({ snapshot }: EnergyBudgetCardProps) {
  const scale = energyBudgetScale(snapshot.expenditureKcal, snapshot.targetKcal);
  const segments = confidenceSegments(snapshot.expenditureConfidence);

  return (
    <Card className="flex flex-col gap-4 p-4" data-testid="expenditure-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-accent-light p-2 text-accent">
            <Zap size={18} strokeWidth={2} />
          </div>
          <h3 className="text-base font-semibold text-text">Energy budget</h3>
        </div>
        <span
          className={cn(
            "inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold",
            snapshot.expenditureKind === "personalised" ? "bg-accent text-white" : "bg-muted text-text-muted",
          )}
          data-testid="expenditure-badge"
        >
          {expenditureBadge(snapshot.expenditureKind)}
        </span>
      </div>

      {scale !== null ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-text-muted">You burn</span>
              <span className="font-semibold text-text" data-testid="expenditure-value">
                {formatNumber(scale.expenditureKcal)} kcal
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="plan-fill-grow h-full rounded-full bg-accent"
                style={{ width: `${Math.round(scale.expenditureFraction * 1000) / 10}%` }}
                data-testid="energy-expenditure-bar"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-text-muted">Your target</span>
              <span className="font-semibold text-text" data-testid="energy-target-value">
                {formatNumber(scale.targetKcal)} kcal
              </span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="plan-fill-grow h-full rounded-full bg-accent-dark"
                style={{ width: `${Math.round(scale.targetFraction * 1000) / 10}%` }}
                data-testid="energy-target-bar"
              />
            </div>
          </div>
          <p
            className={cn(
              "text-sm font-semibold",
              scale.kind === "deficit" && "text-accent",
              scale.kind === "surplus" && "text-warning",
              scale.kind === "maintenance" && "text-text-muted",
            )}
            data-testid="energy-difference"
          >
            {scale.kind === "maintenance"
              ? "Maintenance — your target matches your burn."
              : `Planned ${scale.kind} of ~${formatNumber(scale.differenceKcal)} kcal per day.`}
          </p>
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-text-muted" data-testid="energy-pending">
          Not enough data yet — your energy budget appears once the engine estimates your expenditure.
        </p>
      )}

      <div className="flex items-center gap-2">
        <ConfidenceChip level={snapshot.expenditureConfidence} testId="expenditure-confidence" />
        <span className="flex items-center gap-1" aria-hidden data-testid="energy-confidence-segments">
          {[1, 2, 3].map((index) => (
            <span
              key={index}
              className={cn(
                "inline-block h-1.5 w-5 rounded-full",
                index <= segments ? "bg-accent" : "bg-muted",
              )}
            />
          ))}
        </span>
      </div>

      <p className="text-xs leading-relaxed text-text-muted">
        This estimate sets your starting target; weekly check-ins refine it.
      </p>
    </Card>
  );
}
